import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

export interface PortioningTool {
  id: number
  name: string
  description: string
  imageUrl?: string
  createdAt: string
  updatedAt: string
}

export interface PortioningToolResponse {
  data: PortioningTool
}

export interface PortioningToolsResponse {
  data: PortioningTool[]
  meta: {
    total: number
    pages: number
  }
}

export interface PortioningToolStats {
  totalTools: number
  toolsWithImage: number
}

interface PortioningToolInitialState {
  tools: PortioningTool[]
  tool: PortioningTool | null
  stats: PortioningToolStats
  loading: boolean
  meta: PortioningToolsResponse['meta']
  error: any
  success: string | null
}

const initialState: PortioningToolInitialState = {
  tools: [],
  tool: null,
  stats: {
    totalTools: 0,
    toolsWithImage: 0
  },
  loading: false,
  meta: {
    total: 0,
    pages: 0
  },
  error: null,
  success: null
}

interface PortioningToolQueryOptions {
  take?: number
  skip?: number
  sortBy?: string
  filters?: Record<string, any>
}

export const getPortioningTools = createAsyncThunk<
  PortioningToolsResponse,
  PortioningToolQueryOptions,
  { rejectValue: any; extra: { httpClient: any } }
>('gygPortioningTools/getPortioningTools', async (params, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.get(`portioning-tools`, {
      params
    })

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const createPortioningTool = createAsyncThunk<
  PortioningToolResponse,
  FormData,
  { rejectValue: any; extra: { httpClient: any } }
>('gygPortioningTools/createPortioningTool', async (formData, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.post('portioning-tools', formData)

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const updatePortioningTool = createAsyncThunk<
  PortioningToolResponse,
  { id: number; data: FormData },
  { rejectValue: any; extra: { httpClient: any } }
>('gygPortioningTools/updatePortioningTool', async ({ id, data }, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.patch(`portioning-tools/${id}`, data)

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const getPortioningTool = createAsyncThunk<
  PortioningToolResponse,
  number,
  { rejectValue: any; extra: { httpClient: any } }
>('gygPortioningTools/getPortioningTool', async (id, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.get(`portioning-tools/${id}`)

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const deletePortioningTool = createAsyncThunk<
  { id: number },
  number,
  { rejectValue: any; extra: { httpClient: any } }
>('gygPortioningTools/deletePortioningTool', async (id, { rejectWithValue, extra }) => {
  try {
    await extra.httpClient.delete(`portioning-tools/${id}`)

    return { id }
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const getPortioningToolStats = createAsyncThunk<
  { data: PortioningToolStats },
  { country?: string },
  { rejectValue: any; extra: { httpClient: any } }
>('gygPortioningTools/getPortioningToolStats', async ({ country }, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.get('portioning-tools/stats', {
      params: { country }
    })

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

const gygPortioningToolSlice = createSlice({
  name: 'gygPortioningTools',
  initialState,
  reducers: {
    resetSuccessAndError: state => {
      state.success = null
      state.error = null
    }
  },
  extraReducers: builder => {
    builder.addCase(getPortioningTools.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getPortioningTools.fulfilled, (state, action) => {
      state.loading = false
      state.error = null
      state.success = null
      state.tools = action.payload.data
      state.meta = action.payload.meta
    })
    builder.addCase(getPortioningTools.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to fetch portioning tools'
    })

    builder.addCase(createPortioningTool.pending, state => {
      state.loading = true
      state.error = null
      state.success = null
    })
    builder.addCase(createPortioningTool.fulfilled, (state, action) => {
      state.loading = false

      if (action.payload.data && action.payload.data.id) {
        state.tools.unshift(action.payload.data)
        state.meta.total += 1
      }
      state.success = 'Portioning tool created successfully'
    })
    builder.addCase(createPortioningTool.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to create portioning tool'
    })

    builder.addCase(updatePortioningTool.pending, state => {
      state.loading = true
      state.error = null
      state.success = null
    })
    builder.addCase(updatePortioningTool.fulfilled, state => {
      state.loading = false
      state.success = 'Portioning tool updated successfully'
    })
    builder.addCase(updatePortioningTool.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to update portioning tool'
    })

    builder.addCase(getPortioningTool.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getPortioningTool.fulfilled, (state, action) => {
      state.loading = false
      state.tool = action.payload.data
    })
    builder.addCase(getPortioningTool.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to fetch portioning tool'
    })

    builder.addCase(deletePortioningTool.pending, state => {
      state.loading = true
      state.error = null
      state.success = null
    })
    builder.addCase(deletePortioningTool.fulfilled, (state, action) => {
      state.loading = false
      state.tools = state.tools.filter(t => t.id !== action.payload.id)
      state.meta.total -= 1
      state.success = 'Portioning tool deleted successfully'
    })
    builder.addCase(deletePortioningTool.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to delete portioning tool'
    })

    builder.addCase(getPortioningToolStats.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getPortioningToolStats.fulfilled, (state, action) => {
      state.loading = false
      state.stats = action.payload.data
    })
    builder.addCase(getPortioningToolStats.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to fetch portioning tool stats'
    })
  }
})

export const { resetSuccessAndError } = gygPortioningToolSlice.actions
export default gygPortioningToolSlice.reducer
